import { type ReactNode, createContext, useCallback, useContext, useState } from 'react';

import { ImageAssetWithAltText } from '@fhs/ui';
import { LaunchDarklyFlag, useFlag } from '@fhs-legacy/frontend/src/state/launchdarkly';

import { PICKER_SELECTION_TYPE } from '../components/guide/constants';
import type { PickerSelectionType, Step } from '../components/guide/types';
import { useMenuGuideQuery } from '../queries/use-menu-guide-query';
import { useMenuQueryOpts } from '../queries/use-menu-query-opts';
import type { CustomizationSelections, MenuPickerData, SelectedCustomization } from '../types';

import { useMenuPicker } from './menu-picker';

export type SelectedPicker = {
  id: string;
  slug: string;
  // Derived
  displayName: string;
  priceCents: number;
  baseCalories?: number;

  // Instructions
  instructions?: string;

  // Picker
  pickerSelections?: Record<string, string | null>;

  // PickerAspectOption
  selectedPickerAspectOptionId?: string;
  selectedPickerAspectOption?:
    | null
    | NonNullable<MenuPickerData['pickerAspect']>['options'][number];

  // Customizations
  defaultCustomizationSelections?: CustomizationSelections;
  userCustomizationSelections?: CustomizationSelections;
  resolvedCustomizationSelections?: CustomizationSelections;

  draftCustomizationSelections?: CustomizationSelections;
  requiresCustomization: boolean;

  // Formatted customization options to render on "Make it a combo" section
  selectedCustomizationOptions?: SelectedCustomization[];

  // UI
  image?: ImageAssetWithAltText;
};

export type ComboSelectionContext = {
  isComboEnabled: boolean;
  isComboAvailable: boolean;
  selectedPickers: Record<string, SelectedPicker>;
  isEditingPickers: boolean;
  setIsComboEnabled: (enabled: boolean) => void;
  setSelectedPickers: (pickers: Record<string, SelectedPicker>) => void;
  addOrUpdateSelectedPicker: (step: string, picker: SelectedPicker) => void;
  setIsEditingPickers: (editing: boolean) => void;
  isComboGuideOpen: boolean;
  setIsComboGuideOpen: (value: boolean) => void;
  shouldAddToCartDirectly: boolean;
  setShouldAddToCartDirectly: (value: boolean) => void;
  comboModalDisplayed: boolean;
  setComboModalDisplayed: (value: boolean) => void;
  guideSteps: Array<Step>;
  pickerSelectionType: PickerSelectionType;
  setPickerSelectionType: (value: PickerSelectionType) => void;
};

type ComboSelectionProviderStateProps = {
  children?: ReactNode;
  guideSteps: Array<Step>;
  initialState?: {
    isComboEnabled?: boolean;
    isComboAvailable?: boolean;
    comboSelectedPickers?: Record<string, SelectedPicker>;
  };
};

const comboSelectionContext = createContext<ComboSelectionContext | null>(null);

const ComboSelectionProviderState = ({
  children,
  initialState,
  guideSteps,
}: ComboSelectionProviderStateProps) => {
  const enableComboDefaultSelection = useFlag(
    LaunchDarklyFlag.ENABLE_MENU_DETAILS_DEFAULT_COMBO_SELECTION
  );

  const getInitialPickerSelectionType = () => {
    if (initialState && initialState.isComboEnabled !== undefined) {
      return initialState.isComboEnabled
        ? PICKER_SELECTION_TYPE.COMBO
        : PICKER_SELECTION_TYPE.JUST_SUB;
    }

    return enableComboDefaultSelection
      ? PICKER_SELECTION_TYPE.COMBO
      : PICKER_SELECTION_TYPE.JUST_SUB;
  };

  const [pickerSelectionType, setPickerSelectionType] = useState(getInitialPickerSelectionType);
  const [selectedPickers, setSelectedPickers] = useState<Record<string, SelectedPicker>>(
    initialState?.comboSelectedPickers ?? {}
  );
  const [isEditingPickers, setIsEditingPickers] = useState(false);
  const [isComboGuideOpen, setIsComboGuideOpen] = useState(false);
  const [shouldAddToCartDirectly, setShouldAddToCartDirectly] = useState(false);

  // Used to not display "Make it a Combo" modal more than once if user already opted for just the sub
  const [comboModalDisplayed, setComboModalDisplayed] = useState(false);

  const isComboEnabled = pickerSelectionType === PICKER_SELECTION_TYPE.COMBO;
  const isComboAvailable = initialState?.isComboAvailable ?? true;

  const addOrUpdateSelectedPicker = useCallback(
    (step: string, picker: SelectedPicker) => {
      setSelectedPickers(prevPickers => ({
        ...prevPickers,
        [step]: picker, // Add or replace the picker for the given step
      }));
    },
    [setSelectedPickers]
  );

  const value: ComboSelectionContext = {
    selectedPickers,
    setSelectedPickers,
    addOrUpdateSelectedPicker,
    isEditingPickers,
    setIsEditingPickers,
    isComboEnabled,
    isComboAvailable,
    setIsComboEnabled: () => {}, // TODO: remove/handle this on FHS-4323
    isComboGuideOpen,
    setIsComboGuideOpen,
    shouldAddToCartDirectly,
    setShouldAddToCartDirectly,
    comboModalDisplayed,
    setComboModalDisplayed,
    guideSteps,
    pickerSelectionType,
    setPickerSelectionType,
  };

  return <comboSelectionContext.Provider value={value}>{children}</comboSelectionContext.Provider>;
};

type ComboSelectionProviderProps = {
  children: ReactNode;
  initialState?: {
    isComboEnabled?: boolean;
    comboSelectedPickers?: Record<string, SelectedPicker>;
  };
};
export const ComboSelectionProvider = ({ initialState, children }: ComboSelectionProviderProps) => {
  const { query: menuPickerQuery } = useMenuPicker();
  const comboId = menuPickerQuery.data?.combo?.id ?? '';

  const menuGuideQuery = useMenuGuideQuery({
    enabled: Boolean(comboId),
    comboId,
    ...useMenuQueryOpts(),
  });

  const isComboRequired = menuPickerQuery.data?.combo?.isRequired;

  /**
   * If the menu guide is not available, it might be due to issues like missing or misconfigured combo options.
   * In this case, we assume combos should not be enabled to prevent broken configurations from affecting the UX.
   */
  const isComboMenuGuideAvailable = menuGuideQuery.data?.isAvailable;

  // We want to force combo enabled in case it's required (e.g. Kids Combos)
  const currentComboState =
    isComboMenuGuideAvailable && (isComboRequired ? true : initialState?.isComboEnabled);

  const mergedInitialState = {
    ...initialState,
    // We want to force combo enabled in case it's required (e.g. Kids Combos)
    isComboEnabled: currentComboState,
    // Disable combos if the menu guide is not available, otherwise preserve or enforce the current state.
    isComboAvailable: isComboMenuGuideAvailable,
  };

  return (
    <ComboSelectionProviderState
      initialState={mergedInitialState}
      guideSteps={menuGuideQuery.data?.steps ?? []}
      // Reset combo selection provider state when combo id changes or initialState updates
      key={[comboId, initialState && JSON.stringify(mergedInitialState)].filter(Boolean).join('-')}
    >
      {children}
    </ComboSelectionProviderState>
  );
};

export const useComboSelectionContext = (): ComboSelectionContext => {
  const context = useContext(comboSelectionContext);

  if (!context) {
    throw new Error(
      'useComboSelectionContext can only be called from within a descendant of ComboSelectionProvider'
    );
  }

  return context;
};
